import type { AxiosInstance, AxiosResponse } from 'axios';
import { z } from 'zod';

const handleZodError = (error: z.ZodError) => {
  console.log('ZOD ERROR', error);
  throw new Error('Beklenmedik bir hata oluştu, daha sonra tekrar deneyiniz.');
};

export interface GenericRequest<T extends z.ZodTypeAny, U, X extends boolean> {
  url: string;
  parser: T;
  payload?: U;
  returnData?: X;
}

export interface GenericFormDataRequest<T extends z.ZodTypeAny, X extends boolean> {
  url: string;
  parser: T;
  formData: FormData;
  returnData?: X;
}

export type GenericResponse<T, X extends boolean> = X extends true ? T : AxiosResponse<T>;

export default class GenericAPIService {
  private instance: AxiosInstance;

  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  async get<T extends z.ZodTypeAny, U, X extends boolean>({
    url,
    parser,
    payload,
    returnData,
  }: GenericRequest<T, U, X>): Promise<GenericResponse<z.infer<T>, X>> {
    const res = await this.instance.get<z.infer<T>>(url, {
      ...(payload && { params: payload }),
    });

    try {
      parser.parse(res.data);
    } catch (error) {
      handleZodError(error as z.ZodError);
    }

    return returnData ? (res.data as GenericResponse<z.infer<T>, X>) : (res as GenericResponse<z.infer<T>, X>);
  }

  async post<T extends z.ZodTypeAny, U, X extends boolean>({
    url,
    parser,
    payload,
    returnData,
  }: GenericRequest<T, U, X>): Promise<GenericResponse<z.infer<T>, X>> {
    const res = await this.instance.post<z.infer<T>>(url, payload);

    try {
      parser.parse(res.data);
    } catch (error) {
      handleZodError(error as z.ZodError);
    }

    return returnData ? (res.data as GenericResponse<z.infer<T>, X>) : (res as GenericResponse<z.infer<T>, X>);
  }

  async put<T extends z.ZodTypeAny, U, X extends boolean>({
    url,
    parser,
    payload,
    returnData,
  }: GenericRequest<T, U, X>): Promise<GenericResponse<z.infer<T>, X>> {
    const res = await this.instance.put<z.infer<T>>(url, payload);

    try {
      parser.parse(res.data);
    } catch (error) {
      handleZodError(error as z.ZodError);
    }

    return returnData ? (res.data as GenericResponse<z.infer<T>, X>) : (res as GenericResponse<z.infer<T>, X>);
  }

  async delete<T extends z.ZodTypeAny, U, X extends boolean>({
    url,
    parser,
    payload,
    returnData,
  }: GenericRequest<T, U, X>): Promise<GenericResponse<z.infer<T>, X>> {
    const res = await this.instance.delete<z.infer<T>>(url, {
      ...(payload && { data: payload }),
    });

    try {
      parser.parse(res.data);
    } catch (error) {
      handleZodError(error as z.ZodError);
    }

    return returnData ? (res.data as GenericResponse<z.infer<T>, X>) : (res as GenericResponse<z.infer<T>, X>);
  }

  async postFormData<T extends z.ZodTypeAny, X extends boolean>({
    url,
    parser,
    formData,
    returnData,
  }: GenericFormDataRequest<T, X>): Promise<GenericResponse<z.infer<T>, X>> {
    const res = await this.instance.post<z.infer<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    try {
      parser.parse(res.data);
    } catch (error) {
      handleZodError(error as z.ZodError);
    }

    return returnData ? (res.data as GenericResponse<z.infer<T>, X>) : (res as GenericResponse<z.infer<T>, X>);
  }
}
